// Where a URL ends (CYBORG-1788); ui/src/lib and server cyborg/ hold byte-identical copies.

const HARD_STOP = /[\s<>"`]/u;
const GLUED = /^\(?(?:https?:\/\/|www\.)/iu;
const TRAILING =
  /^(?:[.,:;!?'’”»›。、，…）」』】〉》]|\p{Extended_Pictographic}|\p{Emoji_Modifier}|\uFE0F|\u200D)$/u;
const OPENER: Record<string, string> = { ")": "(", "]": "[", "}": "{" };
const SCHEME = /https?:\/\//giu;

// `tick` is an optional test-only work meter (chars scanned); callers that omit it are unchanged.
type Tick = (units: number) => void;

function count(text: string, start: number, end: number, ch: string, tick?: Tick): number {
  tick?.(end - start);
  let n = 0;
  for (let i = start; i < end; i++) if (text[i] === ch) n++;
  return n;
}

// End of the URL at `start` (<= limit): it stops at \s < > " ` and before `,` `;` `)` glued
// to a new URL; trailing punctuation and unbalanced `)` `]` `}` then go. Linear, no regex stack.
export function urlEnd(text: string, start: number, limit = text.length, tick?: Tick): number {
  let end = start;
  for (; end < limit; end++) {
    const ch = text[end];
    if (HARD_STOP.test(ch)) break;
    const glued = ch === "," || ch === ";" || (ch === ")" && text[end + 1] === "(");
    if (glued && GLUED.test(text.slice(end + 1, end + 10))) break;
  }
  tick?.(end - start);
  return trimUrlEnd(text, start, end, tick);
}

/** Drops trailing punctuation and unbalanced closers from the URL text[start, end). */
export function trimUrlEnd(text: string, start: number, end: number, tick?: Tick): number {
  const counts = new Map<string, number>();
  while (end > start) {
    tick?.(1);
    const low = text.charCodeAt(end - 1);
    const width = low >= 0xdc00 && low <= 0xdfff && end - 2 >= start ? 2 : 1;
    const last = text.slice(end - width, end);
    if (TRAILING.test(last)) {
      end -= width;
      continue;
    }
    const opener = OPENER[last];
    if (!opener) break;
    const closes = counts.get(last) ?? count(text, start, end, last, tick);
    const opens = counts.get(opener) ?? count(text, start, end, opener, tick);
    if (closes <= opens) break;
    counts.set(last, closes - 1).set(opener, opens);
    end -= 1;
  }
  return end;
}

/** Every http(s) URL in `text` (any letter case) with at least one char past `//`. */
export function findUrls(text: string, tick?: Tick): Array<{ start: number; end: number }> {
  const out: Array<{ start: number; end: number }> = [];
  let last = 0;
  for (const match of text.matchAll(SCHEME)) {
    if (match.index < last) continue;
    const end = urlEnd(text, match.index, text.length, tick);
    if (end === match.index + match[0].length) continue;
    out.push({ start: match.index, end });
    last = end;
  }
  return out;
}
